/**
 * thesis.js — WHY A TRADE WAS OPENED, AND WHETHER THAT REASON HELD.
 *
 * Part 1 of the Trades extra context. He asked for it eight times between 2026-08-08 and
 * 2026-08-16; the spec is [[project_trades_context_spec]] and the approved shape is
 * `mockups/SPEKTRX_TradeThesis_Mockup_v0.3.html`.
 *
 * Four things get recorded at open, and the fourth is the one that matters:
 *
 *   text          what you expect, and why
 *   trigger       what made it actionable NOW
 *   expected      target and timeframe
 *   invalidation  the condition under which the thesis is WRONG
 *
 * ⚠ AN INVALIDATION IS NOT A STOP. A stop is a price you leave at; an invalidation is a reason
 * the position should not exist. They coincide often enough to be confused and the difference is
 * the whole value of the record: exiting at SL tells you the price moved, exiting because the
 * thesis broke tells you the reasoning did.
 *
 * ── THE VERDICT IS DERIVED, AND DELIBERATELY HAS NO MOVING PART ─────────────────────────────
 *
 * `thesisVerdict` computes at RENDER time from figures the trade already carries. Nothing is
 * stamped, no background job runs, no state is written. The optional `watch` condition is
 * evaluated against the EXIT, which is a fact on a closed trade.
 *
 * That was a deliberate narrowing in part 1: the exit-derived verdict needs no stored state, so
 * it could not go wrong, and it left the live watcher as a genuinely separate piece of work.
 *
 * ⚠ **PART 1b THEN ADDED THE WATCHER, AND IT IS NOT REDUNDANT.** `scanTradeCrossings` stamps
 * `thesis.firedAt` the first time the price crosses, while the trade is OPEN, and notifies. That
 * catches what the exit alone cannot: a thesis that **broke in July and recovered before he sold
 * in August** exits above the condition, so the derived check says DRIFTED OUT — the app watched
 * the invalidation happen, told him about it, and then forgot. `firedAt` outranks the exit in
 * `thesisVerdict` because it is the earlier and more specific observation. The derived path stays
 * as the answer for every trade that closed before the watcher existed.
 *
 * ── NO MIGRATION, DELIBERATELY ──────────────────────────────────────────────────────────────
 *
 * Trades opened before this shipped get NO field at all, and `thesisVerdict` answers `'none'`.
 * Back-filling a default would manufacture a measurement — the same failure as substituting spot
 * for a missing rate (SYNC-002) or zero for an absent snapshot (CONTRACT-002). A thesis added to
 * an old trade is stamped `recordedAt` = the day it was typed, so a later reader can see it was
 * written after the outcome was known.
 *
 * ⚠ `trades` is a restorable array, so `'thesis'` is in `_IMPORT_KEYS` in the same commit as this
 * file. That list is a DELETION list — see trap #1, which has cost live data twice.
 *
 * (2026-08-16)
 */

/** The verdicts, and the single place their labels are written. */
export const VERDICTS = {
  none:  { label: 'NOT RECORDED',      cls: 'none'  },
  inv:   { label: 'INVALIDATION HIT',  cls: 'inv'   },
  tgt:   { label: 'TARGET HIT',        cls: 'tgt'   },
  drift: { label: 'DRIFTED OUT',       cls: 'drift' },
};

/** The prose fields, in the order they are asked and shown. */
export const THESIS_TEXT_FIELDS = ['text', 'trigger', 'expected', 'invalidation'];

const _s = v => (typeof v === 'string' ? v.trim() : '');

/**
 * A number, or NaN — and **absent is NEVER zero**.
 *
 * ⚠ THIS EXISTS BECAUSE THE FIRST DRAFT GOT IT WRONG THREE TIMES IN THIS FILE. `Number('')` and
 * `Number(null)` are both **0**, and zero is a perfectly valid price. So an untouched watch input
 * became the condition *"price falls below 0"*, and an UNPRICED exit (`r.exit === null` on a row
 * the journal could not price) tested as `0 <= 170` and reported **INVALIDATION HIT on every one
 * of them**. Register theme #3, `|| 0` on netExtCapital / inflation / goldTRY, committed inside
 * the very file whose docblock warns about it. Found by the tests, not by review.
 */
function _num(v) {
  if (v === null || v === undefined || v === '') return NaN;
  const n = Number(v);
  return isFinite(n) ? n : NaN;
}

/** The fields the FORM owns. Anything not here survives an edit; anything here the form decides. */
const FORM_OWNED = new Set(['text', 'trigger', 'expected', 'invalidation', 'watch']);
// ⚠ `firedAt` is deliberately NOT form-owned: the watcher writes it, the author never does, and
// `mergeThesisOnEdit` therefore carries it through an edit. Changing the prose must not erase the
// fact that the condition already fired. Changing the WATCH does — see mergeThesisOnEdit.

/**
 * Is there anything here at all?
 *
 * A thesis with four empty strings and no watch is NOT a thesis — it is the form having been
 * opened and closed. It must read as NOT RECORDED, not as an empty statement of intent.
 */
export function hasThesis(th) {
  if (!th || typeof th !== 'object') return false;
  if (THESIS_TEXT_FIELDS.some(k => _s(th[k]))) return true;
  return !!validWatch(th.watch);
}

/**
 * A usable watch condition, or null.
 *
 * Returns null for anything it cannot evaluate — a missing value, a non-finite one, an unknown
 * direction. **Absent must never read as satisfied**, which is why every caller asks this rather
 * than testing `th.watch` for truthiness.
 */
export function validWatch(w) {
  if (!w || typeof w !== 'object') return null;
  const value = _num(w.value);
  if (!isFinite(value)) return null;
  if (w.dir !== 'below' && w.dir !== 'above') return null;
  const metric = _s(w.metric) || 'price';
  return { metric, dir: w.dir, value };
}

/**
 * Build a thesis from form values, or null when the author wrote nothing.
 *
 * `recordedAt` is supplied by the caller rather than read from a clock here, so a test can pin it
 * and so the one place that knows "now" stays the module that already owns it.
 */
export function buildThesis(form, nowISO, prev) {
  const th = {};
  THESIS_TEXT_FIELDS.forEach(k => { const v = _s(form && form[k]); if (v) th[k] = v; });
  const w = validWatch(form && form.watch);
  if (w) th.watch = w;
  if (!hasThesis(th)) return null;
  th.recordedAt = (prev && prev.recordedAt) || nowISO;
  if (prev && prev.recordedAt) th.editedAt = nowISO;
  return th;
}

/**
 * Merge a form-built thesis over the stored one.
 *
 * ⚠ SAME SHAPE AS `mergeTdOnEdit`, AND FOR THE SAME REASON. `saveT` ends in
 * `Object.assign(t, d)`, and a wholesale replace assembled from a form drops every field the form
 * has never heard of — that is RUN 1's theme #18, and it cost `rateSegments` on a time deposit
 * (₺26,177 → ₺24,940) before anyone noticed. The form owns the four texts and the watch;
 * it has never heard of `recordedAt`.
 *
 * Returns `null` only when the author has cleared everything, which is a deliberate erase.
 */
export function mergeThesisOnEdit(prev, form, nowISO) {
  const next = buildThesis(form, nowISO, prev);
  if (!next) return null;
  // ⚠ CARRY ONLY WHAT THE FORM HAS NEVER HEARD OF. A plain `{...prev, ...next}` looks like the
  // safe direction and is the OPPOSITE failure: `buildThesis` omits a field the author cleared,
  // so the old value would spread straight back in and clearing any single field would be
  // impossible — silently, with the form showing empty and the row still displaying the old text.
  const carried = {};
  if (prev) Object.keys(prev).forEach(k => { if (!FORM_OWNED.has(k)) carried[k] = prev[k]; });
  const out = { ...carried, ...next };
  // ⚠ A NEW CONDITION HAS NOT FIRED YET. `firedAt` is a fact about the OLD watch; carrying it
  // onto a different one would report an invalidation that never happened, and clearing the watch
  // entirely would leave a stamp with nothing to explain it. Same value = same fact, so an edit
  // that leaves the condition alone keeps the stamp.
  const pw = validWatch(prev && prev.watch), nw = validWatch(next.watch);
  const sameWatch = !!pw && !!nw && pw.metric === nw.metric && pw.dir === nw.dir && pw.value === nw.value;
  if (!sameWatch) delete out.firedAt;
  return out;
}

/**
 * HAS THE INVALIDATION FIRED, at this price, right now?
 *
 * Part 1b. Called from `scanTradeCrossings` on every price refresh, for OPEN trades only, and the
 * caller stamps `thesis.firedAt` the first time it answers true. Pure: it does not know about
 * state, time, or whether it has been asked before.
 *
 * ⚠ **ONLY A PRICE WATCH CAN BE CHECKED AGAINST A PRICE.** `validWatch` normalises any metric
 * string, so an RSI condition would otherwise be compared to a share price and fire on nonsense.
 * The form offers price only today; this guard is what makes adding a second metric safe rather
 * than silently wrong.
 *
 * ⚠ **AND A MISSING PRICE IS NOT A ZERO PRICE.** `_num` returns NaN for null / undefined / '',
 * because `Number(null) <= 170` is true and would fire the invalidation on every ticker the feed
 * has not answered for. That exact mistake shipped in this file's first draft.
 */
export function invalidationFired(th, price) {
  const w = validWatch(th && th.watch);
  if (!w || w.metric !== 'price') return false;
  const p = _num(price);
  if (!isFinite(p)) return false;
  return w.dir === 'below' ? p <= w.value : p >= w.value;
}

/**
 * Which of the four verdicts describes how this closed trade ended.
 *
 * ORDER IS LOAD-BEARING. The invalidation is checked BEFORE the exit reason, because a trade can
 * hit its stop *and* have had its thesis broken first, and in that case the thesis is the more
 * useful answer — it is the one that says the reasoning failed rather than the price moved.
 *
 * @param {Object|null} th      the trade's thesis
 * @param {Object} exitFacts    { exit, reason } as the journal row already computes them
 */
export function thesisVerdict(th, exitFacts) {
  if (!hasThesis(th)) return 'none';
  const f = exitFacts || {};
  // ⚠ A CONDITION THAT FIRED WHILE THE TRADE WAS OPEN IS A FACT, EVEN IF THE EXIT DID NOT CROSS.
  // Part 1b stamps `firedAt` from the price feed. Without this branch a thesis that broke in July
  // and recovered before he sold in August reads DRIFTED OUT — the app watched the invalidation
  // happen, told him, and then forgot. The stamp outranks the exit because it is the earlier and
  // more specific observation.
  if (th.firedAt) return 'inv';
  const w = validWatch(th.watch);
  const exit = _num(f.exit);
  if (w && isFinite(exit)) {
    const hit = w.dir === 'below' ? exit <= w.value : exit >= w.value;
    if (hit) return 'inv';
  }
  // `reason` is the exit reason the journal row carries: TP1 / TP2 / SL / Manual / …
  if (/^tp/i.test(_s(f.reason))) return 'tgt';
  return 'drift';
}

/**
 * The condition in words, for the row. Null when there is nothing to say.
 *
 * Deterministic and templated from the stored numbers — never generated prose, the same rule the
 * journal hero context follows.
 */
export function watchSentence(th) {
  const w = validWatch(th && th.watch);
  if (!w) return null;
  return `${w.metric} ${w.dir === 'below' ? 'falls below' : 'rises above'} ${w.value}`;
}
